import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import moment from "moment"
import { getDate } from "modules/intraday/selectors/dates"
import { TIME_FORMAT_TZ } from "helpers/dates"
import { LOADING } from "helpers/data-statuses"
import { downloadXlsx } from "helpers/csv"
import { Creators as DialogCreators } from "modules/dialog/model"

/**
 * Custom hook to handle intraday data refresh workflow
 * Automatically refreshes data when appropriate, or shows manual refresh button
 *
 * @param {Object} params - Configuration object
 * @param {boolean} params.isIntraday - Whether dashboard is in intraday mode
 * @param {boolean} params.intradayDataAvailable - Whether intraday data is available
 * @param {boolean} params.useLatestDate - Whether user is using the latest date
 * @param {Object} params.lastIntradayBatch - The latest intraday batch data
 * @param {Object} params.intradayBatchMetadata - Current batch metadata being displayed
 * @param {Function} params.setIntradayBatchMetadata - Function to update batch metadata
 * @param {Function} params.setPeriod - Function to update period
 * @param {Object} params.currentPeriod - The current period object
 * @param {string} params.adjustedStartDate - The adjusted start date
 * @param {Array} params.trades - Current trades array
 * @param {Function} params.resetTrades - Function to reset trades
 * @param {Function} params.onDownloadData - Function to get download data
 * @param {boolean} params.isPreferencesLoaded - Whether dashboard preferences have been loaded
 * @param {boolean} params.isEndDateIntraday - Whether the current end date is intraday
 * @param {string} params.lastIntradayBatchStatus - Status of the last intraday batch
 * @returns {Object} - Object with refresh handlers, state, and UI properties
 */
export function useIntradayRefresh({
  isIntraday,
  intradayDataAvailable,
  useLatestDate,
  lastIntradayBatch,
  intradayBatchMetadata,
  setIntradayBatchMetadata,
  setPeriod,
  currentPeriod,
  adjustedStartDate,
  trades,
  resetTrades,
  onDownloadData,
  isPreferencesLoaded,
  isEndDateIntraday,
  lastIntradayBatchStatus,
  setUseLatestDate,
}) {
  const dispatch = useDispatch()
  const intradayDate = getDate()

  const newDataAvailable = useMemo(() => {
    const result = isIntraday && lastIntradayBatch?.asOf !== intradayBatchMetadata?.asOf
    return result
  }, [isIntraday, lastIntradayBatch, intradayBatchMetadata])

  const shouldAutoRefresh = useMemo(() => {
    return newDataAvailable && useLatestDate && intradayDataAvailable && trades?.length === 0 && isPreferencesLoaded
  }, [newDataAvailable, useLatestDate, intradayDataAvailable, trades, isPreferencesLoaded])

  const refreshIntradayData = useCallback(() => {
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch.asOf,
      id: lastIntradayBatch.id,
    })
    setUseLatestDate(true)
    setPeriod({
      ...currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [setIntradayBatchMetadata, lastIntradayBatch?.asOf, lastIntradayBatch?.id, setUseLatestDate, setPeriod, currentPeriod, adjustedStartDate, intradayDate])

  const onRefreshPositions = useCallback(() => {
    resetTrades()
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch.asOf,
      id: lastIntradayBatch.id,
    })
    setPeriod({
      ...currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [resetTrades, setIntradayBatchMetadata, lastIntradayBatch, adjustedStartDate, intradayDate, setPeriod, currentPeriod])

  // Show dialog when user has trades and wants to refresh
  const showDialog = useCallback((onConfirm, onDownload, newAsOf) => {
    dispatch(DialogCreators.open({
      title: "Confirm Refresh & Clear All Trades",
      contentText: `There is new intraday data available as of ${newAsOf}. Refreshing will reset your trades. Would you still like to proceed?`,
      buttons: [{
        text: "Download & Refresh",
        type: "secondary",
        action: async () => {
          const { data, fileName } = onDownload()
          await downloadXlsx(fileName, data, {
            type: "intraday-refresh-download",
            location: "dashboard",
          })
          onConfirm()
        },
      }, {
        text: "Refresh",
        type: "primary",
        action: onConfirm,
      }],
    }))
  }, [dispatch])

  const handleIntradayRefresh = useCallback(() => {
    if (trades?.length > 0) {
      const newAsOf = moment(lastIntradayBatch?.asOf, TIME_FORMAT_TZ).format("LTS")
      showDialog(onRefreshPositions, onDownloadData, newAsOf)
    } else {
      refreshIntradayData()
    }
  }, [trades, showDialog, onRefreshPositions, onDownloadData, lastIntradayBatch, refreshIntradayData])

  useEffect(() => {
    if (shouldAutoRefresh) {
      refreshIntradayData()
    }
  }, [shouldAutoRefresh, refreshIntradayData])

  // Determine if we should show the manual refresh button
  const showRefreshButton = useMemo(() => {
    // Show refresh button if:
    // 1. New data is available and won't auto-refresh, OR
    // 2. Viewing historical dates with intraday data available
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    const historicalWithIntradayData = intradayDataAvailable && !isEndDateIntraday
    return newDataNeedsRefresh || historicalWithIntradayData
  }, [newDataAvailable, shouldAutoRefresh, intradayDataAvailable, isEndDateIntraday])

  const refreshButtonTooltip = useMemo(() => {
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    if (newDataNeedsRefresh) {
      return "New intraday data available"
    }
    return "Load intraday data as end date"
  }, [newDataAvailable, shouldAutoRefresh])

  // Determine if we should show the warning icon
  const showWarning = useMemo(() => {
    return isIntraday && !intradayDataAvailable && lastIntradayBatchStatus !== LOADING
  }, [isIntraday, intradayDataAvailable, lastIntradayBatchStatus])

  return {
    newDataAvailable,
    showRefreshButton,
    showWarning,
    handleIntradayRefresh,
    refreshIntradayData,
    refreshButtonTooltip,
  }
}
